// Dashboard — 我的仓库列表 — Fluent UI v9
//
// 布局规范:
//   1. 顶部工具栏 — Title + 搜索 + Refresh + New
//   2. Card 网格(hoverable + onClick 进详情)
//   3. 空态
//   4. Loading(Spinner)
//   5. 底部分页(0 条不显示)
import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Badge,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Title3,
} from '@fluentui/react-components';
import {
  AddRegular,
  ArrowClockwiseRegular,
  SearchRegular,
  LockClosedRegular,
  GlobeRegular,
  AppFolderRegular,
  DismissRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  DatabaseRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { reposApi } from '@/api/repos';
import type { PublicRepo } from '@/api/types';
import { explainError } from '@/api/client';
import { HelpPopover } from '@/components/HelpPopover';
import { SimpleList } from '@/components/SimpleList';
import { RepoDetailDialog } from '@/components/RepoDetailDialog';
import { NewRepoDialog } from '@/components/NewRepoDialog';

const PAGE_SIZE = 9;

const useStyles = makeStyles({
  // 占满 contentBody 可视区高度;内部 body 滚动,分页固定贴底
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXL,
    flexWrap: 'wrap',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    margin: 0,
    fontWeight: 600,
  },
  spacer: {
    flex: 1,
  },
  searchInput: {
    width: '320px',
    maxWidth: '100%',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: tokens.spacingVerticalL,
  },
  // 列表行内用图标/附加信息(与仓库管理 SimpleList 风格一致)
  repoIcon: {
    fontSize: '22px',
    color: tokens.colorBrandForeground1,
  },
  extraText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  // 分页固定贴底:body flex:1 吸收全部剩余高度,分页紧跟其后贴在可视区底部
  pagination: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '10px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pageInfo: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
});

export function DashboardPage() {
  const styles = useStyles();
  const [query, setQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [queryError, setQueryError] = useState<string | null>(null);
  // 仓库详情对话框
  const [detail, setDetail] = useState<{ owner: string; name: string } | null>(null);
  // 新建仓库对话框
  const [newRepoOpen, setNewRepoOpen] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['my-repos'],
    queryFn: () => reposApi.myRepos(),
  });

  // 搜索框变化 → 跳回第 1 页
  useEffect(() => {
    setPageIndex(1);
  }, [query]);

  // 错误提示(无 ToastProvider,用内联 MessageBar + console.error 替代 message.error)
  useEffect(() => {
    if (error) {
      const msg = explainError(error);
      console.error(msg);
      setQueryError(msg);
    } else {
      setQueryError(null);
    }
  }, [error]);

  const allRepos: PublicRepo[] = data?.repos ?? [];
  const filtered = allRepos.filter(
    (r) =>
      !query ||
      r.name.toLowerCase().includes(query.toLowerCase()) ||
      (r.description?.toLowerCase().includes(query.toLowerCase()) ?? false),
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((pageIndex - 1) * PAGE_SIZE, pageIndex * PAGE_SIZE);

  function openRepo(repo: PublicRepo) {
    setDetail({ owner: repo.owner, name: repo.name });
  }

  return (
    <div className={styles.root}>
      {/* 1. 顶部工具栏 */}
      <div className={styles.toolbar}>
        <Title3 as="h2" className={styles.title}>
          <AppFolderRegular />
          我的仓库
        </Title3>
        <Input
          className={styles.searchInput}
          contentBefore={<SearchRegular />}
          contentAfter={
            query ? (
              <Button
                appearance="transparent"
                size="small"
                icon={<DismissRegular />}
                aria-label="清除搜索"
                onClick={() => setQuery('')}
              />
            ) : undefined
          }
          placeholder="搜索仓库名或描述"
          value={query}
          onChange={(_, d) => setQuery(d.value)}
        />
        <HelpPopover content="按仓库名或描述模糊搜索你自己的仓库,留空显示全部。" />
        <Button
          appearance="outline"
          icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
          disabled={isFetching}
          onClick={() => refetch()}
        >
          刷新
        </Button>
        <div className={styles.spacer} />
        <Button
          appearance="primary"
          icon={<AddRegular />}
          onClick={() => setNewRepoOpen(true)}
        >
          新建仓库
        </Button>
      </div>

      {/* 查询错误条 */}
      {queryError && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{queryError}</MessageBarBody>
        </MessageBar>
      )}

      {/* 2-3. 列表(SimpleList 风格:媒体图标+标题+描述),loading/空态由组件接管 */}
      <div className={styles.body}>
        <SimpleList
          loading={isLoading}
          emptyText={query ? '没有匹配的仓库' : '还没有仓库,新建一个吧'}
          pageSize={Number.POSITIVE_INFINITY}
          items={paged.map((repo) => ({
            key: String(repo.id),
            media: <DatabaseRegular className={styles.repoIcon} />,
            title: (
              <>
                <span>{repo.name}</span>
                {repo.visibility === 'private' ? (
                  <Badge appearance="filled" color="subtle" icon={<LockClosedRegular />}>
                    private
                  </Badge>
                ) : (
                  <Badge appearance="filled" color="brand" icon={<GlobeRegular />}>
                    public
                  </Badge>
                )}
              </>
            ),
            description: repo.description || '(无描述)',
            extra: (
              <span className={styles.extraText}>
                {new Date(repo.updated_at).toLocaleDateString('zh-CN')}
              </span>
            ),
            onClick: () => openRepo(repo),
          }))}
        />
      </div>

      {/* 4. 分页(0 条不显示) */}
      {filtered.length > 0 && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={pageIndex <= 1}
            onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
          />
          <Text className={styles.pageInfo}>
            第 {pageIndex} / {totalPages} 页 · 共 {filtered.length} 个
          </Text>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={pageIndex >= totalPages}
            onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
          />
        </div>
      )}

      {/* 仓库详情对话框 */}
      <RepoDetailDialog
        owner={detail?.owner ?? null}
        name={detail?.name ?? null}
        onClose={() => setDetail(null)}
      />

      {/* 新建仓库对话框 */}
      <NewRepoDialog
        open={newRepoOpen}
        onClose={() => setNewRepoOpen(false)}
        onCreated={(repo) => {
          setNewRepoOpen(false);
          setDetail({ owner: repo.owner, name: repo.name });
        }}
      />
    </div>
  );
}
